import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import {
  SETTING_API_02,
  SETTING_API_08,
  SETTING_API_20,
  SETTING_API_21,
} from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_VALUE_ZERO,
  CON_MAX_NAME_LENGTH,
  CON_MAX_DESCRIPTION_LENGTH,
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import { Messages, getInputMaxLengthMessage, MessageCategory } from "../../utils/sys/Messages.tsx";

// Define interfaces
interface MainCategory {
  maincatcode: string | number;
  maincatname: string;
}

interface Category {
  catcode: string | number;
  catname: string;
}

interface SubCategory {
  subcatcode: string | number;
  subcatname: string;
}

interface GenericName {
  genericcode?: string | number;
  genericname: string;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  descrip?: string;
}

interface UserParams {
  STAFFMAPLOCODE: string;
  USERNAME: string;
  COMPCODE: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: GenericName;
  actionText?: string;
  selectedMainCategory?: string | number;
  selectedCategory?: string | number;
  selectedSubCategory?: string | number;
}

interface FormData {
  genericname: string;
  maincatcode: string | number;
  catcode: string | number;
  subcatcode: string | number;
  descrip: string;
  genericcode: string | number;
}

interface LoadingState {
  mainCategories: boolean;
  categories: boolean;
  subcategories: boolean;
  editData: boolean;
}

interface ErrorState {
  genericname?: string;
  maincatcode?: string;
  catcode?: string;
  subcatcode?: string;
  descrip?: string;
}

function CreateGenaricName(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageData } = usePageData();

  const locationState = location.state as LocationState || {};
  const {
    selectedItem,
    actionText,
    selectedMainCategory,
    selectedCategory,
    selectedSubCategory,
  } = locationState;
  const isEdit = actionText === "Edit";

  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { STAFFMAPLOCODE, USERNAME, COMPCODE } = (SYS_USER.USER_PARAM as UserParams) || {};

  const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [existingGenericNames, setExistingGenericNames] = useState<GenericName[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<LoadingState>({
    mainCategories: false,
    categories: false,
    subcategories: false,
    editData: false,
  });
  const [error, setError] = useState<ErrorState>({});
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING | "info">(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [formData, setFormData] = useState<FormData>({
    genericname: "",
    maincatcode: "",
    catcode: "",
    subcatcode: "",
    descrip: "",
    genericcode: CON_VALUE_ZERO,
  });

  useEffect(() => setPageData({ title: "Generic Name" }), [setPageData]);

  // Load main categories and existing generic names
  useEffect(() => {
    const loadInitialData = async (): Promise<void> => {
      try {
        setIsLoading((prev) => ({ ...prev, mainCategories: true }));

        // Load main categories
        const mains = await externalgetApi(SETTING_API_20, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });
        const mainCategoriesData = Array.isArray(mains) ? mains : [];
        setMainCategories(mainCategoriesData);

        // Set default main category if not in edit mode and no preselected values
        if (!isEdit && mainCategoriesData.length > 0 && !selectedMainCategory && !selectedItem) {
          setFormData(prev => ({
            ...prev,
            maincatcode: mainCategoriesData[0].maincatcode,
          }));
        }

        // Load existing generic names for duplicate check
        const existing = await externalgetApi(SETTING_API_21, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });
        setExistingGenericNames(Array.isArray(existing) ? existing : []);

        setIsLoading((prev) => ({ ...prev, mainCategories: false }));
      } catch (err) {
        console.error("Error loading initial data:", err);
        setIsLoading((prev) => ({ ...prev, mainCategories: false }));
        showAlert(Messages.genericName.error.fetchInitialFailed, MessageCategory.ERROR);
      }
    };

    if (COMPCODE && STAFFMAPLOCODE) {
      loadInitialData();
    }
  }, [COMPCODE, STAFFMAPLOCODE, isEdit, selectedMainCategory, selectedItem]);

  // Load data for edit mode - SIMPLIFIED VERSION
  useEffect(() => {
    if (selectedItem && isEdit) {
      // Set form data directly from selectedItem (which should have all the info from View page)
      setFormData({
        genericname: selectedItem.genericname || "",
        maincatcode: selectedItem.maincatcode || "",
        catcode: selectedItem.catcode || "",
        subcatcode: selectedItem.subcatcode || "",
        descrip: selectedItem.descrip || "",
        genericcode: selectedItem.genericcode || CON_VALUE_ZERO,
      });

      // If we have maincatcode, fetch categories for it
      if (selectedItem.maincatcode) {
        const fetchCategoriesForEdit = async (): Promise<void> => {
          setIsLoading((prev) => ({ ...prev, categories: true }));
          try {
            const res = await externalgetApi(SETTING_API_02, "get", {
              COMPCODE,
              LOCCODE: STAFFMAPLOCODE,
              MAINCATCODE: selectedItem.maincatcode,
              STS: CON_STATUS_ACTIVE,
            });
            setCategories(Array.isArray(res) ? res : []);
          } catch (err) {
            console.error("Error fetching categories for edit:", err);
            setCategories([]);
          } finally {
            setIsLoading((prev) => ({ ...prev, categories: false }));
          }
        };
        fetchCategoriesForEdit();
      }

      // If we have maincatcode and catcode, fetch subcategories
      if (selectedItem.maincatcode && selectedItem.catcode) {
        const fetchSubcategoriesForEdit = async (): Promise<void> => {
          setIsLoading((prev) => ({ ...prev, subcategories: true }));
          try {
            const res = await externalgetApi(SETTING_API_08, "get", {
              MAINCATCODE: selectedItem.maincatcode,
              CATCODE: selectedItem.catcode,
              STS: CON_STATUS_ACTIVE,
            });
            setSubcategories(Array.isArray(res) ? res : []);
          } catch (err) {
            console.error("Error fetching subcategories for edit:", err);
            setSubcategories([]);
          } finally {
            setIsLoading((prev) => ({ ...prev, subcategories: false }));
          }
        };
        fetchSubcategoriesForEdit();
      }
    } else if (
      !isEdit &&
      selectedMainCategory &&
      selectedCategory &&
      selectedSubCategory
    ) {
      // For create mode with pre-selected values from filters
      setFormData({
        genericname: "",
        maincatcode: selectedMainCategory || "",
        catcode: selectedCategory || "",
        subcatcode: selectedSubCategory || "",
        descrip: "",
        genericcode: CON_VALUE_ZERO,
      });

      // Fetch categories for the selected main category
      if (selectedMainCategory) {
        const fetchCategoriesForCreate = async (): Promise<void> => {
          setIsLoading((prev) => ({ ...prev, categories: true }));
          try {
            const res = await externalgetApi(SETTING_API_02, "get", {
              COMPCODE,
              LOCCODE: STAFFMAPLOCODE,
              MAINCATCODE: selectedMainCategory,
              STS: CON_STATUS_ACTIVE,
            });
            const categoriesData = Array.isArray(res) ? res : [];
            setCategories(categoriesData);
          } catch (err) {
            console.error("Error fetching categories for create:", err);
            setCategories([]);
          } finally {
            setIsLoading((prev) => ({ ...prev, categories: false }));
          }
        };
        fetchCategoriesForCreate();
      }

      // Fetch subcategories for the selected category
      if (selectedMainCategory && selectedCategory) {
        const fetchSubcategoriesForCreate = async (): Promise<void> => {
          setIsLoading((prev) => ({ ...prev, subcategories: true }));
          try {
            const res = await externalgetApi(SETTING_API_08, "get", {
              MAINCATCODE: selectedMainCategory,
              CATCODE: selectedCategory,
              STS: CON_STATUS_ACTIVE,
            });
            const subcategoriesData = Array.isArray(res) ? res : [];
            setSubcategories(subcategoriesData);
          } catch (err) {
            console.error("Error fetching subcategories for create:", err);
            setSubcategories([]);
          } finally {
            setIsLoading((prev) => ({ ...prev, subcategories: false }));
          }
        };
        fetchSubcategoriesForCreate();
      }
    }
  }, [
    selectedItem,
    isEdit,
    selectedMainCategory,
    selectedCategory,
    selectedSubCategory,
    COMPCODE,
    STAFFMAPLOCODE,
  ]);

  // Fetch categories when main category changes (for create mode - when user manually changes)
  useEffect(() => {
    if (!formData.maincatcode || isEdit) return;

    const fetchCategoriesForMainCategory = async (): Promise<void> => {
      setIsLoading((prev) => ({ ...prev, categories: true }));
      try {
        const res = await externalgetApi(SETTING_API_02, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          MAINCATCODE: formData.maincatcode,
          STS: CON_STATUS_ACTIVE,
        });
        const categoriesData = Array.isArray(res) ? res : [];
        setCategories(categoriesData);
        
        // Set default category if categories exist and no category is selected
        if (categoriesData.length > 0 && !formData.catcode) {
          setFormData((prev) => ({
            ...prev,
            catcode: categoriesData[0].catcode,
          }));
        } else {
          // Reset category and subcategory when main category changes
          setFormData((prev) => ({
            ...prev,
            catcode: "",
            subcatcode: "",
          }));
          setSubcategories([]);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategories([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, categories: false }));
      }
    };

    fetchCategoriesForMainCategory();
  }, [formData.maincatcode, isEdit, COMPCODE, STAFFMAPLOCODE]);

  // Fetch subcategories when category changes (for create mode - when user manually changes)
  useEffect(() => {
    if (!formData.maincatcode || !formData.catcode || isEdit) return;

    const fetchSubcategoriesForCategory = async (): Promise<void> => {
      setIsLoading((prev) => ({ ...prev, subcategories: true }));
      try {
        const res = await externalgetApi(SETTING_API_08, "get", {
          MAINCATCODE: formData.maincatcode,
          CATCODE: formData.catcode,
          STS: CON_STATUS_ACTIVE,
        });
        const subcategoriesData = Array.isArray(res) ? res : [];
        setSubcategories(subcategoriesData);
        
        // Set default subcategory if subcategories exist
        if (subcategoriesData.length > 0) {
          setFormData((prev) => ({
            ...prev,
            subcatcode: subcategoriesData[0].subcatcode,
          }));
        } else {
          // Reset subcategory when category changes and no subcategories
          setFormData((prev) => ({ ...prev, subcatcode: "" }));
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubcategories([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, subcategories: false }));
      }
    };

    fetchSubcategoriesForCategory();
  }, [formData.catcode, formData.maincatcode, isEdit]);

  // Handle main category change
  const handleMainCategoryChange = (e: SelectChangeEvent<unknown>): void => {
    const value = e.target.value as string;
    setFormData((prev) => ({
      ...prev,
      maincatcode: value,
      catcode: "",
      subcatcode: "",
    }));
    setCategories([]);
    setSubcategories([]);
    setError((prev) => ({ ...prev, maincatcode: "", catcode: "", subcatcode: "" }));
  };

  // Handle category change
  const handleCategoryChange = (e: SelectChangeEvent<unknown>): void => {
    const value = e.target.value as string;
    setFormData((prev) => ({
      ...prev,
      catcode: value,
      subcatcode: "",
    }));
    setSubcategories([]);
    setError((prev) => ({ ...prev, catcode: "", subcatcode: "" }));
  };

  // Handle subcategory change
  const handleSubcategoryChange = (e: SelectChangeEvent<unknown>): void => {
    const value = e.target.value as string;
    setFormData((prev) => ({ ...prev, subcatcode: value }));
    setError((prev) => ({ ...prev, subcatcode: "" }));
  };

  const handleGenericNameInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmed = value.replace(/^\s+/, "");

    let errorMsg = "";
    if (trimmed.length === CON_MAX_NAME_LENGTH) {
      errorMsg = getInputMaxLengthMessage( CON_MAX_NAME_LENGTH);
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setFormData((prev) => ({ ...prev, [name]: trimmed }));
  };

  const handleDescriptionInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmed = value.replace(/^\s+/, "");

    let errorMsg = "";
    if (trimmed.length === CON_MAX_DESCRIPTION_LENGTH) {
      errorMsg = getInputMaxLengthMessage( CON_MAX_DESCRIPTION_LENGTH);
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setFormData((prev) => ({ ...prev, [name]: trimmed }));
  };

  const handleGenericNameBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    const trimmed = value.trim();

    let errorMsg = "";
    if (!trimmed) {
      errorMsg = Messages.genericName.warning.nameRequired;
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setFormData((prev) => ({ ...prev, [name]: trimmed }));
  };

  const showAlert = (msg: string, type: MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING | "info"): void => {
    setAlertMessage(msg);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleConfirmClick = async (): Promise<void> => {
    const {
      genericname,
      subcatcode,
      catcode,
      maincatcode,
      descrip,
      genericcode,
    } = formData;
    const trimmedGenericName = genericname.trim();
    const trimmedDesc = descrip.trim();

    // Clear previous errors
    setError({});

    let validationErrors: ErrorState = {};
    let hasError = false;

    // Required field validation
    if (!trimmedGenericName) {
      validationErrors.genericname = Messages.genericName.warning.nameRequired;
      hasError = true;
    }

    if (!subcatcode) {
      validationErrors.subcatcode = Messages.genericName.warning.subcategoryRequired;
      hasError = true;
    }

    if (!catcode) {
      validationErrors.catcode = Messages.genericName.warning.categoryRequired;
      hasError = true;
    }

    if (!maincatcode) {
      validationErrors.maincatcode = Messages.genericName.warning.mainCategoryRequired;
      hasError = true;
    }

    // Leading space validation (trailing spaces are trimmed on save, not validated)
    if (genericname !== trimmedGenericName) {
      validationErrors.genericname = Messages.genericName.warning.leadingSpaces;
      hasError = true;
    }

    const descripNoLeading = (descrip || "").replace(/^\s+/, "");
    if (descrip !== descripNoLeading) {
      validationErrors.descrip = Messages.genericName.warning.leadingSpaces;
      hasError = true;
    }

    // Character length validation
    if (trimmedGenericName.length > CON_MAX_NAME_LENGTH) {
      validationErrors.genericname = getInputMaxLengthMessage( CON_MAX_NAME_LENGTH);
      hasError = true;
    }

    if (trimmedDesc.length > CON_MAX_DESCRIPTION_LENGTH) {
      validationErrors.descrip = getInputMaxLengthMessage( CON_MAX_DESCRIPTION_LENGTH);
      hasError = true;
    }

    setError(validationErrors);

    if (hasError) {
      showAlert(Messages.genericName.warning.requiredFields, MessageCategory.ERROR);
      return;
    }

    // Duplicate Check
    const duplicate = existingGenericNames.some(
      (i) =>
        i.genericname.toLowerCase() === trimmedGenericName.toLowerCase() &&
        i.subcatcode == subcatcode &&
        i.genericcode !== genericcode
    );
    if (duplicate) {
      showAlert(Messages.genericName.warning.alreadyExists, MessageCategory.WARNING);
      return;
    }

    setIsButtonDisabled(true);

    try {
      const payload = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        genericname: trimmedGenericName,
        subcatcode: Number(subcatcode),
        descrip: trimmedDesc,
        genericcode: isEdit ? genericcode : CON_VALUE_ZERO,
        sts: CON_STATUS_ACTIVE,
        enuser: USERNAME || "Admin",
        MODE: isEdit ? "UPDATE" : "CREATE" as "UPDATE" | "CREATE",
      };

      await externalpostApi(SETTING_API_21, payload);

      showAlert(
        isEdit ? Messages.genericName.success.update : Messages.genericName.success.create,
        MessageCategory.SUCCESS
      );

      setTimeout(() => navigate("/vgenericnames"), 2000);
    } catch (err) {
      console.error("Save error:", err);
      const errorMsg =
        (err as any)?.response?.data?.message ||
        (isEdit
          ? Messages.genericName.error.updateFailed
          : Messages.genericName.error.createFailed);
      showAlert(errorMsg, MessageCategory.ERROR);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const handleCancel = (): void => {
    // Clear any error messages and navigate back to the previous page
    setError({});
    navigate("/vgenericnames");
  };

  const goBackToView = (): void => {
    navigate("/vgenericnames");
  };

  return (
    <Box sx={styles.box_add_page_container}>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to All Items" arrow>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link 
            underline="hover" 
            sx={styles.breadcrumb_link}
            component="button"
          >
            Home
          </Link>
          <Link 
            sx={styles.breadcrumb_link} 
            underline="hover"
            component="button"
          >
            Settings
          </Link>
          <Typography sx={styles.breadcrumb_link_page}>
            {isEdit ? "Edit Generic Name" : "Create Generic Name"}
          </Typography>
        </Breadcrumbs>
      </Box>

      <AppBar sx={styles.appbar}>
        <Typography sx={styles.appbar_title}>
          {isEdit ? "Edit Generic Name" : "Create Generic Name"}
        </Typography>
      </AppBar>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{
          ...styles.add_form_page_container,
          maxWidth: "1200px",
          marginTop: "20px",
          padding: 2,
        }}
      >
        <Grid container spacing={2}>
          {/* MAIN CATEGORY */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl
              fullWidth
              size="small"
              required
              error={!!error.maincatcode}
            >
              <InputLabel id="lbl_maincategory">Main Category</InputLabel>
              <Select
                id="sec_maincatcode"
                value={formData.maincatcode || ""}
                label="Main Category"
                onChange={handleMainCategoryChange}
                disabled={
                  isButtonDisabled ||
                  isLoading.mainCategories ||
                  isLoading.editData ||
                  isEdit
                }
                renderValue={(selected) => {
                  if (!selected) return <em>Select Main Category</em>;
                  const mainCat = mainCategories.find(
                    (m) => m.maincatcode == selected
                  );
                  return mainCat ? mainCat.maincatname : `ID: ${selected}`;
                }}
              >
                {isLoading.mainCategories ? (
                  <MenuItem disabled value="">
                    Loading main categories...
                  </MenuItem>
                ) : (
                  mainCategories.map((main) => (
                    <MenuItem key={main.maincatcode} value={main.maincatcode}>
                      {main.maincatname}
                    </MenuItem>
                  ))
                )}
              </Select>
              {error.maincatcode && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ fontSize: "12px", mt: 0.5 }}
                >
                  {error.maincatcode}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} />

          {/* CATEGORY */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl
              fullWidth
              size="small"
              required
              error={!!error.catcode}
            >
              <InputLabel id="lbl_category">Category</InputLabel>
              <Select
                id="sec_catcode"
                value={formData.catcode || ""}
                label="Category"
                onChange={handleCategoryChange}
                disabled={
                  isButtonDisabled ||
                  !formData.maincatcode ||
                  isLoading.categories ||
                  isLoading.editData ||
                  isEdit
                }
                renderValue={(selected) => {
                  if (!selected) return <em>Select Category</em>;
                  const cat = categories.find((c) => c.catcode == selected);
                  return cat ? cat.catname : `ID: ${selected}`;
                }}
              >
                {isLoading.categories ? (
                  <MenuItem disabled value="">
                    Loading categories...
                  </MenuItem>
                ) : categories.length === 0 && formData.maincatcode ? (
                  <MenuItem disabled value="">
                    No categories available for selected main category
                  </MenuItem>
                ) : (
                  categories.map((cat) => (
                    <MenuItem key={cat.catcode} value={cat.catcode}>
                      {cat.catname}
                    </MenuItem>
                  ))
                )}
              </Select>
              {error.catcode && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ fontSize: "12px", mt: 0.5 }}
                >
                  {error.catcode}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} />

          {/* SUB CATEGORY */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl
              fullWidth
              size="small"
              required
              error={!!error.subcatcode}
            >
              <InputLabel id="lbl_subcategory">Subcategory</InputLabel>
              <Select
                id="sec_subcatcode"
                value={formData.subcatcode || ""}
                label="Subcategory"
                onChange={handleSubcategoryChange}
                disabled={
                  isButtonDisabled ||
                  !formData.maincatcode ||
                  isLoading.categories ||
                  isLoading.editData ||
                  isEdit
                }
                renderValue={(selected) => {
                  if (!selected) return <em>Select Subcategory</em>;
                  const subcat = subcategories.find(
                    (s) => s.subcatcode == selected
                  );
                  return subcat ? subcat.subcatname : `ID: ${selected}`;
                }}
              >
                {isLoading.subcategories ? (
                  <MenuItem disabled value="">
                    Loading subcategories...
                  </MenuItem>
                ) : subcategories.length === 0 && formData.catcode ? (
                  <MenuItem disabled value="">
                    No subcategories available for selected category
                  </MenuItem>
                ) : (
                  subcategories.map((sub) => (
                    <MenuItem key={sub.subcatcode} value={sub.subcatcode}>
                      {sub.subcatname}
                    </MenuItem>
                  ))
                )}
              </Select>
              {error.subcatcode && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ fontSize: "12px", mt: 0.5 }}
                >
                  {error.subcatcode}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} />

          {/* GENERIC NAME */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              id="txt_genericname"
              label="Generic Name"
              variant="outlined"
              fullWidth
              size="small"
              required
              name="genericname"
              value={formData.genericname}
              onChange={handleGenericNameInput}
              onBlur={handleGenericNameBlur}
              error={!!error.genericname}
              helperText={error.genericname}
              inputProps={{ maxLength: CON_MAX_NAME_LENGTH }}
              FormHelperTextProps={{
                sx: { fontSize: "12px", color: "red" },
              }}
              disabled={isButtonDisabled || isLoading.editData}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} />

          {/* DESCRIPTION */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              id="txt_descrip"
              label="Description"
              variant="outlined"
              fullWidth
              size="small"
              name="descrip"
              value={formData.descrip}
              onChange={handleDescriptionInput}
              error={!!error.descrip}
              helperText={error.descrip}
              inputProps={{ maxLength: CON_MAX_DESCRIPTION_LENGTH }}
              FormHelperTextProps={{
                sx: { fontSize: "12px", color: "red" },
              }}
              multiline
              rows={1}
              disabled={isButtonDisabled || isLoading.editData}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={styles.add_form_page_button_box}>
        <Button
          id="btn_cancel"
          variant="outlined"
          sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
          onClick={handleCancel}
          disabled={isButtonDisabled || isLoading.editData}
        >
          Cancel
        </Button>

        <Button
          id="btn_confirm/modify"
          variant="contained"
          sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
          onClick={handleConfirmClick}
          disabled={isButtonDisabled || isLoading.editData}
        >
          {isEdit ? "Modify" : "Confirm"}
        </Button>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
}

export default CreateGenaricName;